import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/shared/Navbar/Navbar";
import Footer from "../../components/shared/Footer/Footer";
import "./AllSolution.css";
import SolutionDetailsHero from "../../components/pages/SolutionDetails/SolutionDetailsHero/SolutionDetailsHero";
import SolutionDetailsDescription from "../../components/pages/SolutionDetails/SolutionDetailsDescription/SolutionDetailsDescription";
import SolutionDetailsFeatures from "../../components/pages/SolutionDetails/SolutionDetailsFeatures/SolutionDetailsFeatures";
import SolutionDetailsApplications from "../../components/pages/SolutionDetails/SolutionDetailsApplications/SolutionDetailsApplications";
import SolutionDetailsProducts from "../../components/pages/SolutionDetails/SolutionDetailsProducts/SolutionDetailsProducts";
import SolutionDetailsDownloads from "../../components/pages/SolutionDetails/SolutionDetailsDownloads/SolutionDetailsDownloads";
import AllSolutionsRequestNewProject from "../../components/pages/AllSolutions/AllSolutionsRequestNewProject/AllSolutionsRequestNewProject";
import Partners from "../../components/pages/About/Partners/Partners";
import { getSolutionDetailBySlug } from "../../data/solutionsDetailData";
import { getPublicSolutionByKey } from "../../api/solutions";
import { filterPdfRowsByProductSlug, mapPublicProductPdfs, pickPublicPdfArray } from "../../utils/productPdfs";

const SolutionDetails = () => {
  const { i18n } = useTranslation();
  const { solutionSlug } = useParams();
  const fallbackSolution = getSolutionDetailBySlug(solutionSlug);
  const [apiSolution, setApiSolution] = useState(null);
  const [activeDescriptionTab, setActiveDescriptionTab] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      const language = String(i18n.language || "en").toLowerCase();
      try {
        const data = await getPublicSolutionByKey(solutionSlug, language);
        if (isMounted) setApiSolution(data || null);
      } catch {
        if (isMounted) setApiSolution(null);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [solutionSlug, i18n.language]);

  const solution = useMemo(() => {
    if (!apiSolution) return fallbackSolution;
    const solutionPdfHints = [solutionSlug, apiSolution?.solutionKey, fallbackSolution?.solutionKey];
    const mergedSolutionPdfs = mapPublicProductPdfs(
      filterPdfRowsByProductSlug(pickPublicPdfArray(apiSolution, fallbackSolution), ...solutionPdfHints)
    );

    return {
      ...(fallbackSolution || {}),
      ...apiSolution,
      cardIcon: apiSolution?.cardIcon || fallbackSolution?.cardIcon || "",
      logo: apiSolution?.logo || fallbackSolution?.logo || "",
      logoUrl: apiSolution?.logoUrl || fallbackSolution?.logoUrl || "",
      heroLogo: apiSolution?.heroLogo || fallbackSolution?.heroLogo || "",
      heroLogoUrl: apiSolution?.heroLogoUrl || fallbackSolution?.heroLogoUrl || "",
      pdfs: mergedSolutionPdfs,
    };
  }, [apiSolution, fallbackSolution, solutionSlug]);

  useEffect(() => {
    setActiveDescriptionTab(0);
  }, [solution?.id, solution?.solutionKey, solution?.slug]);

  if (!solution) {
    return <Navigate to="/allsolutions" replace />;
  }

  return (
    <div className="allsolution">
      <div className="allsolution-content-guide">
        <Navbar theme="dark" />
        <SolutionDetailsHero solution={solution} />
        <SolutionDetailsDescription
          solution={solution}
          activeTabIndex={activeDescriptionTab}
          onActiveTabChange={setActiveDescriptionTab}
        />
        <SolutionDetailsFeatures solution={solution} activeTabIndex={activeDescriptionTab} />
        <SolutionDetailsApplications solution={solution} />
        <SolutionDetailsProducts solution={solution} />
        <SolutionDetailsDownloads
          solution={solution}
          items={(solution?.pdfs || []).map((p) => ({
            id: p.id,
            title: p.title,
            href: p.href,
          }))}
          downloadsDescription="Technical PDFs and documents for this solution."
        />
        <AllSolutionsRequestNewProject />
        <Partners />
      </div>
      <Footer />
    </div>
  );
};

export default SolutionDetails;
